/**
 * Noten (Roadmap F-22, Stufe 4): Punkte 0-15 oder Noten 1-6, gewichteter
 * Durchschnitt je Fach und gesamt.
 *
 * Gerechnet wird intern in Punkten; Noten werden ueber die uebliche
 * Oberstufen-Zuordnung umgerechnet (15 P = 0,67 -> 1, 0 P = 6), stetig, damit
 * Durchschnitte nicht springen. So bleibt alles richtig, wenn jemand das
 * Notensystem wechselt.
 */
import { skalaStufe } from '@/domain/rating';
import type { Grade, Notensystem } from '@/domain/types';

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Note bei 1 Punkt - darunter wird linear bis 6 (0 P) weitergerechnet. */
const NOTE_BEI_1P = 16 / 3;

export function punkteAusNote(note: number): number {
  if (note > NOTE_BEI_1P) return clamp((6 - note) / (6 - NOTE_BEI_1P), 0, 1);
  return clamp(17 - 3 * note, 0, 15);
}

export function noteAusPunkten(punkte: number): number {
  if (punkte < 1) return 6 - clamp(punkte, 0, 1) * (6 - NOTE_BEI_1P);
  return clamp((17 - punkte) / 3, 1, 6);
}

export function inPunkte(g: Pick<Grade, 'value' | 'system'>): number {
  return g.system === 'punkte' ? g.value : punkteAusNote(g.value);
}

/** Gueltiger Wert im jeweiligen System? Punkte ganzzahlig, Noten auch mit Tendenz (2,3). */
export function gueltigerWert(wert: number, system: Notensystem): boolean {
  if (!Number.isFinite(wert)) return false;
  return system === 'punkte' ? Number.isInteger(wert) && wert >= 0 && wert <= 15 : wert >= 1 && wert <= 6;
}

/** Gewichteter Durchschnitt in Punkten, `null` ohne Noten. */
export function schnittPunkte(noten: Grade[]): number | null {
  const gewicht = noten.reduce((s, g) => s + g.weight, 0);
  if (gewicht <= 0) return null;
  return noten.reduce((s, g) => s + inPunkte(g) * g.weight, 0) / gewicht;
}

/** Gesamtschnitt: Mittel der Fachschnitte - jedes Fach zaehlt gleich. */
export function gesamtSchnitt(fachSchnitte: (number | null)[]): number | null {
  const werte = fachSchnitte.filter((w): w is number => w !== null);
  return werte.length === 0 ? null : werte.reduce((a, b) => a + b, 0) / werte.length;
}

/** Wert im gewuenschten System, deutsch formatiert: "11,3 P" oder "2,0". */
export function formatNote(punkte: number, system: Notensystem, nachkomma = 1): string {
  const wert = system === 'punkte' ? punkte : noteAusPunkten(punkte);
  const text = wert.toLocaleString('de-DE', { minimumFractionDigits: nachkomma, maximumFractionDigits: nachkomma });
  return system === 'punkte' ? `${text} P` : text;
}

/** Stufe der Bewertungsskala (1-10) fuer einen Punktwert - 15 P = 10, 0 P = 1. */
export function skalaVonPunkten(punkte: number): number {
  return skalaStufe(1 + (punkte / 15) * 9);
}

/** Uebliche Gewichte je Art - aenderbar beim Eintragen. */
export const NOTEN_ARTEN: { art: string; gewicht: number }[] = [
  { art: 'Klausur', gewicht: 2 },
  { art: 'Mündlich', gewicht: 1 },
  { art: 'Test', gewicht: 1 },
  { art: 'Sonstiges', gewicht: 1 },
];
